/**
 * React Query hook for the Networks screen.
 *
 * Polls every 30 seconds matching the legacy dashboard cadence. The
 * query key includes the active tab so switching tabs triggers a fresh
 * fetch immediately, while the previous tab's data is kept in cache
 * for instant back-navigation.
 *
 * 404 from the backend means NOT_CONFIGURED — the hook normalises this
 * to { configured: false, ... } rather than surfacing an error, so the
 * screen can render the "not connected" empty state without an error
 * boundary.
 */
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { fetchNetworkTab, NetworkData, NetworkTab } from '@/api/networks';

const NOT_CONFIGURED_FALLBACK: NetworkData = {
  configured: false,
  status: 'unknown',
  activeNodes: 0,
  earnings24hUsd: 0,
  topNodes: [],
  countries: []
};

export function useNetworkTab(tab: NetworkTab) {
  return useQuery<NetworkData, AxiosError>({
    queryKey: ['adminNetworks', tab],
    queryFn: async () => {
      try {
        return await fetchNetworkTab(tab);
      } catch (err) {
        const axiosErr = err as AxiosError;
        if (axiosErr?.response?.status === 404) {
          return NOT_CONFIGURED_FALLBACK;
        }
        throw err;
      }
    },
    refetchInterval: 30_000,
    staleTime: 15_000
  });
}
